import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import GenericTable from '../../../../components/tables/GenericTable';
import Pagination from '../../../../layout/Pagination';
import { fetchTherapyOccurrenceStatusLogs } from '../../api/therapyOccurrencesApi';
import StatusFlag from '../../../../components/elements/StatusFlag';
import { getOccurrenceStatusTone } from '../../../../shared/utils/statusTones';

const statusLabel = (status) => {
  const map = {
    unavailable: 'Nedostupna',
    available: 'Dostupna',
    taken: 'Preuzeta',
    saved: 'Sacuvana',
    reserved: 'Rezervisana',
    missed: 'Propustena',
    unassigned: 'Nedodeljeno',
  };
  return map[status] || status || '-';
};

const formatOccurrenceStatus = (status, secondaryStatus) => {
  if (!status) return '';

  const label = statusLabel(status);

  if (secondaryStatus === 'reserved') {
    return `${label} (R)`;
  }

  return label;
};

// Status kao obojena oznaka (isto pravilo boja kao u tabelama).
const renderOccurrenceStatusFlag = (status, secondaryStatus) =>
  status ? (
    <StatusFlag tone={getOccurrenceStatusTone(status)}>
      {formatOccurrenceStatus(status, secondaryStatus)}
    </StatusFlag>
  ) : (
    '-'
  );

const OccurrenceStatusLogsModal = ({ isOpen, occurrence, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const latestLogsRequestRef = useRef(0);

  const resetLogsState = useCallback(() => {
    latestLogsRequestRef.current += 1;
    setLogs([]);
    setCurrentPage(1);
    setTotalPages(1);
    setMessage('');
    setLoading(false);
  }, []);

  useEffect(() => {
    resetLogsState();
  }, [occurrence?._id, resetLogsState]);

  useEffect(() => {
    if (!isOpen) {
      resetLogsState();
    }
  }, [isOpen, resetLogsState]);

  const loadLogs = useCallback(async (page = currentPage) => {
    if (!isOpen || !occurrence?._id) return;

    const requestId = latestLogsRequestRef.current + 1;
    latestLogsRequestRef.current = requestId;
    setLoading(true);

    const { data, ok, message } = await fetchTherapyOccurrenceStatusLogs({
      occurrenceId: occurrence._id,
      page,
      limit: 10,
    });
    if (requestId !== latestLogsRequestRef.current) {
      return;
    }
    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;
      setLogs(data.logs || []);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
    } else if (message) {
      setMessage(message);
      setLogs([]);
      setTotalPages(1);
    }
    setLoading(false);
  }, [currentPage, isOpen, occurrence?._id]);

  useEffect(() => {
    loadLogs(currentPage);
  }, [loadLogs]);

  const columns = useMemo(
    () => [
      {
        key: 'fromStatus',
        label: 'Sa statusa',
        render: (_, row) =>
          renderOccurrenceStatusFlag(
            row.fromStatus ?? row.toStatus,
            row.fromSecondaryStatus
          ),
      },
      {
        key: 'toStatus',
        label: 'Na status',
        render: (_, row) =>
          renderOccurrenceStatusFlag(row.toStatus, row.toSecondaryStatus),
      },
      {
        key: 'changedByName',
        label: 'Izmenio',
        render: (_, row) => row.changedByName || row.changedByEmail || '-',
      },
      { key: 'note', label: 'Napomena', render: (v) => v || '-' },
      {
        key: 'changeDate',
        label: 'Datum izmene',
        render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '-'),
      },
      {
        key: 'changedAt',
        label: 'Datum evidencije',
        render: (v) => (v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '-'),
      },
    ],
    []
  );

  return (
    <Modal
      className="user_therapies_modal"
      width={900}
      open={isOpen}
      onCancel={onClose}
      closable
      footer={null}
      title="Istorija statusa instance"
      destroyOnHidden
    >
      <Spin spinning={loading}>
        <GenericTable
          data={logs}
          columns={columns}
          emptyState={{ icon: '/assets/icons/no-users.svg', title: 'Nema istorije promena' }}
        />
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      </Spin>
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </Modal>
  );
};

OccurrenceStatusLogsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  occurrence: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default OccurrenceStatusLogsModal;
